import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormSelect, FormTextField } from 'components/common';
import { ApplicationFormPageEnum } from '../types';
import { previousNameQuestions } from './questions';
import { useNavigateBack } from 'hooks';
import { IPreviousNameForm } from './types';
import { ISelectOption } from 'components/common/Select/types';
import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';
import { getCurrentRoute } from 'utils/location';

export const PreviousName = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IPreviousNameForm>({
    defaultValues: {
      title: null,
      firstName: '',
      lastName: '',
    },
  });

  const formState = form.watch();

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: IPreviousNameForm) => {
    const questions = Object.entries(formData).map(([key, answer]) => ({
      question: previousNameQuestions[key].labelKey
        ? intl.formatMessage({ id: previousNameQuestions[key].labelKey })
        : '',
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.PERSONAL_DETAILS);
    setSearchParams(searchParams);
  };

  const { title: titleQuestion, firstName: firstNameQuestion, lastName: lastNameQuestion } = previousNameQuestions;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={firstNameQuestion.labelKey} tagName={'h4'} />
          <FormattedMessage id={firstNameQuestion.explanationKey} tagName={'p'} />

          <div className={'grid grid-cols-3 gap-4'}>
            <FormSelect
              name={'title'}
              options={titleQuestion.options}
              className={'col-span-1'}
              selectProps={{
                isOptionEqualToValue: (option: ISelectOption, selectedOptionValue: string) =>
                  option.value === selectedOptionValue,
                value: formState.title,
              }}
              selectInputProps={{
                placeholder: intl.formatMessage({ id: 'placeholders.title' }),
              }}
              rules={{ required: requiredFieldErrorMessage }}
            />
            <FormTextField
              name={'firstName'}
              rules={{ required: requiredFieldErrorMessage }}
              textFieldProps={{ className: 'col-span-2' }}
            />
          </div>
        </section>

        <section>
          <FormattedMessage id={lastNameQuestion.labelKey} tagName={'h4'} />
          <FormattedMessage id={lastNameQuestion.explanationKey} tagName={'p'} />

          <FormTextField name={'lastName'} rules={{ required: requiredFieldErrorMessage }} />
        </section>

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
